import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { comprobanteService } from '../services/comprobanteService';

interface RespuestaExigencia {
  readonly userId: string;
  readonly rige: boolean;
}

/**
 * Consulta la exigencia para una identidad ya autenticada y entrega la respuesta solo mientras esa
 * identidad siga vigente: el `cancelar` devuelto descarta cualquier respuesta posterior (logout o
 * cambio de actor). Sin identidad no consulta: la función es de `authenticated` y antes de iniciar
 * sesión solo respondería 401. Un fallo real con sesión se trata como vigente (la base decide igual).
 */
export function consultarExigenciaDeIdentidad(
  userId: string | null,
  aplicar: (respuesta: RespuestaExigencia) => void,
  consultar: () => Promise<boolean> = () => comprobanteService.getExigenciaVigente(),
): () => void {
  if (!userId) return () => undefined;
  let vigente = true;
  consultar()
    .then((rige) => { if (vigente) aplicar({ userId, rige }); })
    .catch(() => { if (vigente) aplicar({ userId, rige: true }); });
  return () => { vigente = false; };
}

/** Una respuesta solo vale para la identidad que la pidió; para cualquier otra, sigue sin saberse. */
export function exigenciaParaIdentidad(respuesta: RespuestaExigencia | null, userId: string | null): boolean | null {
  return userId !== null && respuesta?.userId === userId ? respuesta.rige : null;
}

export interface ExigenciaComprobantes {
  /** Nulo mientras no hay sesión o mientras carga. */
  readonly rige: boolean | null;
  /** Pregunta de nuevo ahora (sin esperar la caducidad) y actualiza `rige`. Falla cerrado: error = rige. */
  readonly revalidar: () => Promise<boolean | null>;
}

/**
 * ¿Ya rige la exigencia? Una consulta por identidad autenticada (no por render). La app no conoce ni
 * muestra la fecha de entrada en vigor (H18): solo sabe si ya rige. Mientras la respuesta sea "aún
 * no", vuelve a preguntar al volver a la pestaña (el servicio acota la frecuencia) y quien guarda
 * puede llamar `revalidar` justo antes de escribir: cubre la sesión que cruza la fecha sin cambiar de foco.
 */
export function useExigenciaConRevalidacion(): ExigenciaComprobantes {
  const { authUser } = useAuth();
  const userId = authUser?.id ?? null;
  const [respuesta, setRespuesta] = useState<RespuestaExigencia | null>(null);
  const [revalidaciones, setRevalidaciones] = useState(0);
  const exigencia = exigenciaParaIdentidad(respuesta, userId);

  useEffect(() => consultarExigenciaDeIdentidad(userId, setRespuesta), [userId, revalidaciones]);

  useEffect(() => {
    if (exigencia !== false) return;
    const alVolver = () => {
      if (document.visibilityState === 'visible') setRevalidaciones((n) => n + 1);
    };
    document.addEventListener('visibilitychange', alVolver);
    window.addEventListener('focus', alVolver);
    return () => {
      document.removeEventListener('visibilitychange', alVolver);
      window.removeEventListener('focus', alVolver);
    };
  }, [exigencia]);

  const revalidar = useCallback(async (): Promise<boolean | null> => {
    if (!userId) return null;
    const rige = await comprobanteService.getExigenciaVigente({ forzar: true }).catch(() => true);
    setRespuesta({ userId, rige });
    return rige;
  }, [userId]);

  return { rige: exigencia, revalidar };
}

/** ¿Ya rige la exigencia? Nulo mientras no hay sesión o mientras carga. */
export function useExigenciaComprobantes(): boolean | null {
  return useExigenciaConRevalidacion().rige;
}
